/** True when filter is exactly "*" (special required + literal-asterisk mode). */
export function isEntityFieldStarFilter(rawQuery: string): boolean {
    return rawQuery.trim() === '*';
}

/** Normalize label/value text for case-insensitive substring matching. */
export function normalizeEntityFieldFilterText(text: string): string {
    return (text ?? '').toString().replace(/\u00A0\*/g, '').trim().toLowerCase();
}

/** True when a non-empty filter should dim fields and hide empty sections. */
export function isEntityFieldFilterActive(rawQuery: string): boolean {
    return rawQuery.trim().length > 0;
}

/** Flatten a field value into searchable text (refs, arrays, primitives). */
export function entityFieldValueToSearchText(value: unknown): string {
    if (value === undefined || value === null) return '';
    if (typeof value === 'boolean') return value ? 'true yes' : 'false no';
    if (typeof value === 'number' || typeof value === 'bigint') return String(value);
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) {
        return value.map(entityFieldValueToSearchText).filter(Boolean).join(' ');
    }
    if (typeof value === 'object') {
        const o = value as Record<string, unknown>;
        const parts = [
            o.Id,
            o.id,
            o.DisplayName,
            o.displayName,
            o.Name,
            o.name,
            o.$ref,
            o.EntityType,
            o.entityType,
            o.UserPrincipalName,
            o.userPrincipalName,
        ].filter((p) => p !== undefined && p !== null && String(p).trim() !== '');
        if (parts.length > 0) {
            return parts.map((p) => String(p)).join(' ');
        }
        try {
            return JSON.stringify(value);
        } catch {
            return String(value);
        }
    }
    return String(value);
}

/** Whether label, id, or value text contains a literal asterisk. */
export function entityFieldContainsLiteralAsterisk(
    label: string,
    value: unknown,
    extraCandidates: string[] = []
): boolean {
    const sources = [label, ...extraCandidates, entityFieldValueToSearchText(value)];
    return sources.some((s) => String(s ?? '').includes('*'));
}

export interface EntityFieldFilterMatchOptions {
    required?: boolean;
}

/** Whether a field label and/or value matches the filter query. */
export function entityFieldMatchesFilter(
    label: string,
    value: unknown,
    rawQuery: string,
    extraCandidates: string[] = [],
    options: EntityFieldFilterMatchOptions = {}
): boolean {
    const trimmed = rawQuery.trim();
    if (!trimmed) return true;

    if (isEntityFieldStarFilter(trimmed)) {
        if (options.required === true) return true;
        return entityFieldContainsLiteralAsterisk(label, value, extraCandidates);
    }

    const query = trimmed.toLowerCase();

    const candidates = [label, ...extraCandidates, entityFieldValueToSearchText(value)]
        .map(normalizeEntityFieldFilterText)
        .filter(Boolean);

    return candidates.some((candidate) => candidate.includes(query));
}

export type EntityFieldFilterAttr = {
    id?: string;
    displayName?: string;
    required?: boolean;
};

export type EntityFieldFilterEntry = [EntityFieldFilterAttr, unknown];

/** Whether a section should remain visible while filtering (at least one field matches). */
export function entityFieldSectionHasMatch(entries: EntityFieldFilterEntry[], rawQuery: string): boolean {
    if (entries.length === 0) return false;
    if (!isEntityFieldFilterActive(rawQuery)) return true;
    return entries.some(([attr, value]) =>
        entityFieldEntryMatchesFilter(attr, value, rawQuery)
    );
}

function entityFieldEntryMatchesFilter(
    attr: EntityFieldFilterAttr,
    value: unknown,
    rawQuery: string
): boolean {
    return entityFieldMatchesFilter(
        (attr.displayName || attr.id || '').toString(),
        value,
        rawQuery,
        [(attr.id ?? '').toString()].filter(Boolean),
        { required: attr.required === true }
    );
}

/** Count matching vs total fields while a filter is active. */
export function countEntityFieldFilterMatches(
    entries: EntityFieldFilterEntry[],
    rawQuery: string
): { matching: number; total: number } {
    const total = entries.length;
    if (!isEntityFieldFilterActive(rawQuery)) {
        return { matching: total, total };
    }
    const matching = entries.filter(([attr, value]) =>
        entityFieldEntryMatchesFilter(attr, value, rawQuery)
    ).length;
    return { matching, total };
}

export const ENTITY_FIELD_FILTER_DIMMED_CLASS = 'entity-field-filter-dimmed';

/** Grid.Col / wrapper props for non-matching fields when a filter is active. */
export function getEntityFieldFilterColProps(
    label: string,
    value: unknown,
    rawQuery: string,
    extraCandidates: string[] = [],
    options: EntityFieldFilterMatchOptions = {}
): { className?: string } {
    if (!isEntityFieldFilterActive(rawQuery)) return {};
    if (entityFieldMatchesFilter(label, value, rawQuery, extraCandidates, options)) return {};
    return { className: ENTITY_FIELD_FILTER_DIMMED_CLASS };
}
